import { useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { RelayChatExtension } from '@inference.engine/agentic-react';
import { TAXONOMY_CHANGED_PART } from '@inference.engine/taxonomy/web';
import { taxonomyChangedSchema } from '@/entities/taxonomy-dataset';
import { applyTaxonomyChange } from '../lib/applyTaxonomyChange';

interface Options {
  datasetId: string;
  /** Read when a change lands, so it reflects the grid's edits at that moment. */
  getDirtyIds: () => ReadonlySet<string>;
  onChanged?: (recordIds: string[]) => void;
}

/** The chat extensions that carry the taxonomy agent's writes onto the page. */
export function useTaxonomyAgentExtensions({
  datasetId,
  getDirtyIds,
  onChanged,
}: Options): RelayChatExtension[] {
  const queryClient = useQueryClient();
  const latest = useRef({ getDirtyIds, onChanged });
  latest.current = { getDirtyIds, onChanged };

  return useMemo<RelayChatExtension[]>(
    () => [
      {
        part: `data-${TAXONOMY_CHANGED_PART}`,
        onPart: (data) => {
          const parsed = taxonomyChangedSchema.safeParse(data);
          if (!parsed.success || parsed.data.datasetId !== datasetId) return;
          const { changedIds } = applyTaxonomyChange(
            queryClient,
            parsed.data,
            latest.current.getDirtyIds(),
          );
          if (changedIds.length > 0) latest.current.onChanged?.(changedIds);
        },
      },
    ],
    [queryClient, datasetId],
  );
}
